import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Loader2, Heart, Check, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Cover } from "@/components/Cover";
import { api, fmtTime, formatApiError } from "@/lib/api";
import { fmtBytes } from "@/lib/offline";

const SOURCE = { onedrive: "OneDrive", upload: "Resonance", plex: "Plex", webdav: "FileJump" };

const Group = ({ g, keep, onKeep, onResolve, busy }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid={`dupe-group-${g.key}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-base font-medium" data-testid="dupe-group-title">{g.title}</p>
        <p className="truncate text-sm text-zinc-500">{g.artist} · {g.tracks.length} copies · {fmtBytes(g.wasted_bytes)} in extra copies</p>
      </div>
      <button onClick={onResolve} disabled={busy} data-testid={`dupe-resolve-${g.key}`}
        className="flex h-9 items-center gap-2 rounded-full bg-zinc-900 px-4 text-xs font-medium text-white hover:bg-zinc-700 disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}Keep selected, hide {g.tracks.length - 1} other{g.tracks.length - 1 === 1 ? "" : "s"}
      </button>
    </div>
    <div className="mt-4 space-y-1.5">
      {g.tracks.map((t) => {
        const selected = keep === t.id;
        return (
          <label key={t.id} data-testid={`dupe-track-${t.id}`} className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition-colors ${selected ? "border-emerald-300 bg-emerald-50/60" : "border-zinc-100 hover:border-zinc-300"}`}>
            <input type="radio" name={g.key} checked={selected} onChange={() => onKeep(t.id)} data-testid={`dupe-keep-${t.id}`} className="accent-zinc-900" />
            <Cover albumId={t.album_id} alt="" className="h-10 w-10 shrink-0" iconSize={14} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{t.title} {t.starred && <Heart size={11} className="inline fill-amber-500 text-amber-500" />}</p>
              <p className="truncate font-mono text-[11px] text-zinc-500">
                <Link to={`/album/${t.album_id}`} className="hover:underline">{t.album || "Unknown album"}</Link> · {SOURCE[t.source] || t.source} · {fmtTime(t.duration)} · {t.play_count} plays
              </p>
            </div>
            <span className={`shrink-0 rounded px-2 py-0.5 font-mono text-[11px] ${t.lossless ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-600"}`} data-testid={`dupe-quality-${t.id}`}>{t.quality}</span>
            <span className="w-16 shrink-0 text-right font-mono text-[11px] text-zinc-500">{fmtBytes(t.size)}</span>
            {t.id === g.suggested_keep && <span className="flex shrink-0 items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800" data-testid={`dupe-suggested-${t.id}`}><Sparkles size={10} />best</span>}
          </label>
        );
      })}
    </div>
  </div>
);

export default function Duplicates() {
  const [data, setData] = useState(null);
  const [keep, setKeep] = useState({});
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    setData(null);
    const { data } = await api.get("/dupes");
    setData(data);
    setKeep(Object.fromEntries(data.groups.map((g) => [g.key, g.suggested_keep])));
  }, []);
  useEffect(() => { load(); }, [load]);

  const [progress, setProgress] = useState(null);
  const resolve = async (groups) => {
    setBusy(groups.length === 1 ? groups[0].key : "all");
    const BATCH = 20;
    let hidden = 0, done = 0, failed = null;
    if (groups.length > 1) setProgress({ done: 0, total: groups.length, hidden: 0 });
    for (let i = 0; i < groups.length && !failed; i += BATCH) {
      const slice = groups.slice(i, i + BATCH);
      try {
        const payload = slice.map((g) => ({ keep: keep[g.key], hide: g.tracks.map((t) => t.id).filter((id) => id !== keep[g.key]) }));
        const { data: r } = await api.post("/dupes/resolve", { groups: payload });
        hidden += r.hidden;
        done += slice.length;
        if (groups.length > 1) setProgress({ done, total: groups.length, hidden });
      } catch (e) { failed = formatApiError(e); }
    }
    if (failed) toast.error(failed, { description: hidden ? `${hidden} duplicate${hidden === 1 ? "" : "s"} were hidden before this error.` : undefined });
    else toast.success(`Hid ${hidden} duplicate song${hidden === 1 ? "" : "s"}`, { description: "Favourites, play counts and playlist entries moved to the kept copy. Files were not touched." });
    try { await load(); } catch { /* list refresh failed; user can re-scan */ }
    setProgress(null);
    setBusy(null);
  };

  const ProgressCard = progress && (
    <div className="mb-6 rounded-lg border border-zinc-200 bg-white p-5" data-testid="dupes-progress" aria-live="polite">
      <div className="flex items-center justify-between gap-4 text-sm">
        <p className="flex items-center gap-2 font-medium"><Loader2 size={14} className="animate-spin" />Hiding duplicates…</p>
        <p className="font-mono text-xs text-zinc-500" data-testid="dupes-progress-text">{progress.done} of {progress.total} groups · {progress.hidden} song{progress.hidden === 1 ? "" : "s"} hidden</p>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-zinc-100">
        <div className="h-full rounded-full bg-zinc-900 transition-[width] duration-300" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} data-testid="dupes-progress-bar" />
      </div>
    </div>
  );

  if (!data) return progress ? <div>{ProgressCard}</div> : <Loader2 className="animate-spin text-zinc-400" />;
  return (
    <div>
      <PageHeader eyebrow="Tools" title="Duplicate songs" testid="duplicates-title">
        <button onClick={load} disabled={!!busy} data-testid="dupes-refresh" className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 text-sm hover:border-zinc-900 disabled:opacity-50"><RefreshCw size={14} />Re-scan</button>
        {data.total > 0 && (
          <button onClick={() => resolve(data.groups)} disabled={!!busy} data-testid="dupes-resolve-all"
            className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm font-medium text-white hover:bg-zinc-700 disabled:opacity-50">
            {busy === "all" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}{data.total === 1 ? "Keep the best in this group" : `Keep the best in all ${data.total} groups`}
          </button>
        )}
      </PageHeader>
      {ProgressCard}
      <p className="-mt-4 mb-8 max-w-2xl text-sm text-zinc-500" data-testid="dupes-summary">
        {data.total ? <>Found <strong className="text-zinc-900">{data.duplicate_tracks}</strong> duplicate song{data.duplicate_tracks === 1 ? "" : "s"} in {data.total} group{data.total === 1 ? "" : "s"} — same title and artist, within 3 seconds in length.
          The best-quality copy is pre-selected (lossless first, then bitrate). Hiding never deletes files: hidden songs leave the library, their favourites, play counts and playlist spots move to the kept copy.</>
          : "Songs with the same title and artist (within 3 seconds in length) show up here."}
      </p>
      {data.total === 0 ? <EmptyState icon={Copy} title="No duplicates found" text="Your library has one copy of every song. Re-scan after adding music." />
        : <div className="space-y-5" data-testid="dupes-list">
          {data.groups.map((g) => <Group key={g.key} g={g} keep={keep[g.key]} onKeep={(id) => setKeep((k) => ({ ...k, [g.key]: id }))} onResolve={() => resolve([g])} busy={busy === g.key || busy === "all"} />)}
        </div>}
    </div>
  );
}
